import { StorageProvider } from './StorageProvider';
import { MemoryStorage } from './MemoryStorage';

export class IndexedDBStorage implements StorageProvider {
  private db: IDBDatabase | null = null;
  private memoryFallback: MemoryStorage;

  constructor(
    private dbName: string = 'CasioEmuWeb',
    private storeName: string = 'kv',
  ) {
    this.memoryFallback = new MemoryStorage();
  }

  async init(): Promise<void> {
    try {
      this.db = await this.openDB();
    } catch {
      this.db = null;
    }
  }

  async get(key: string): Promise<Uint8Array | null> {
    if (!this.db) {
      return this.memoryFallback.get(key);
    }
    return new Promise<Uint8Array | null>((resolve, reject) => {
      const tx = this.db!.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const req = store.get(key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const data = req.result as Uint8Array | undefined;
        resolve(data !== undefined ? data.slice() : null);
      };
    });
  }

  async set(key: string, data: Uint8Array): Promise<void> {
    if (!this.db) {
      return this.memoryFallback.set(key, data);
    }
    return new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const req = store.put(data, key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
  }

  async delete(key: string): Promise<void> {
    if (!this.db) {
      return this.memoryFallback.delete(key);
    }
    return new Promise<void>((resolve, reject) => {
      const tx = this.db!.transaction(this.storeName, 'readwrite');
      const store = tx.objectStore(this.storeName);
      const req = store.delete(key);
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve();
    });
  }

  async list(prefix: string): Promise<string[]> {
    if (!this.db) {
      return this.memoryFallback.list(prefix);
    }
    return new Promise<string[]>((resolve, reject) => {
      const tx = this.db!.transaction(this.storeName, 'readonly');
      const store = tx.objectStore(this.storeName);
      const range = IDBKeyRange.bound(prefix, prefix + '\uffff');
      const req = store.openCursor(range);
      const keys: string[] = [];
      req.onerror = () => reject(req.error);
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          keys.push(cursor.key as string);
          cursor.continue();
        } else {
          resolve(keys);
        }
      };
    });
  }

  isAvailable(): boolean {
    return this.db !== null;
  }

  getStorageType(): 'indexeddb' | 'memory' {
    return this.db !== null ? 'indexeddb' : 'memory';
  }

  private openDB(): Promise<IDBDatabase> {
    return new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(this.dbName, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }
      };
      req.onerror = () => reject(req.error);
      req.onsuccess = () => resolve(req.result);
    });
  }
}
